import "./BartrackLineDetails.scss";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Table from "../../../../components/table/Table";
import LineDefault from "../../../../images/LineDefault.png";
import { useEffect, useState } from "react";
import getBartrackQualitySummary from "../../../../utility/getBartrackQualitySummary";
import noDatas from "../../../../components/lotties/noDatas.json";
import Lottie from "lottie-react";
import getBartrackPoursSummary from "../../../../utility/getBartrackPoursSummary";
import dayjs from "dayjs";
import Skeleton from "../../../../components/skeletonLoader/SkeletonLoader";

function BartrackLineDetails() {
  const location = useLocation();
  const { line, orgUUID } = location.state || {};
  const { bartrackUUID } = line;
  const navigate = useNavigate();
  const { t } = useTranslation("bartrack");
  const [stats, setStats] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    if (orgUUID && bartrackUUID) {
      fetchQualitySummary({ uuid: orgUUID, bartrackUuid: bartrackUUID });
      fetchPoursSummary({ uuid: orgUUID });
    }
  }, [orgUUID, bartrackUUID]);

  async function fetchQualitySummary({ uuid, bartrackUuid, bins = 720, nbDays = 30 }) {
    try {
      setIsDataLoading(true);
      const today = dayjs();
      const startDate = today.subtract(nbDays, "day");
      const data = await getBartrackQualitySummary(
        uuid,
        bartrackUuid,
        startDate.toDate(),
        today.toDate(),
        bins,
      );

      setStats((prev) => ({ ...prev, ...data.stats }));
      setIsDataLoading(false);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchPoursSummary({ uuid, nbDays = 30 }) {
    try {
      const today = dayjs();
      const startDate = today.subtract(nbDays, "day");
      const newPoursSummary = await getBartrackPoursSummary(
        uuid,
        startDate.toDate(),
        today.toDate(),
      );

      const matchedLine = newPoursSummary?.pumpsData?.find(
        (pump) => pump.line_id === line.bartrackId,
      );

      setStats((prev) => ({
        ...prev,
        pouredQuantity: matchedLine?.pouredQuantity
          ? (matchedLine?.pouredQuantity / 1000).toFixed(2)
          : 0,
      }));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  function StatCard({ title, subtitle, value, isLoading, unit = "" }) {
    return (
      <div className="stat-card">
        <p className="stat-card-title">{title}</p>
        {subtitle && <p className="stat-card-subtitle">{subtitle}</p>}

        {isLoading ? (
          <Skeleton className="stat-card-skeleton-value" />
        ) : (
          <p className="stat-card-value">{value ? `${value} ${unit}` : "N/A"}</p>
        )}
      </div>
    );
  }

  return (
    <div className="bar-track-line">
      <section className="bar-track-line-back-container">
        <button onClick={() => navigate(-1)}>{t("back")}</button>
      </section>

      <section className="bar-track-line-details">
        {window.innerWidth > 1024 && (
          <div className="bar-track-line-details-logo">
            <img src={line.imageUrl || LineDefault} />
          </div>
        )}

        <div className="bar-track-line-details-values">
          {window.innerWidth <= 1024 ? (
            <div className="bar-track-line-details-container">
              <div className="bar-track-line-details-logo">
                <img src={line.imageUrl || LineDefault} />
              </div>
              <div className="bar-track-line-details-values-name">
                <div className="bar-track-line-details-values-name">
                  <p className="bar-track-line-details-values-name-title">{line.name}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bar-track-line-details-values-name">
              <p className="bar-track-line-details-values-name-title">{line.name}</p>
            </div>
          )}

          <div className="bar-track-line-details-values-stats">
            <StatCard
              title={t("temperature")}
              subtitle={`(${t("last_30_days")})`}
              value={stats?.avgTemp}
              isLoading={isDataLoading}
              unit={"°"}
            />
            <StatCard
              title={t("humidity")}
              subtitle={`(${t("last_30_days")})`}
              value={stats?.avgHumi}
              isLoading={isDataLoading}
              unit={"%"}
            />
            <StatCard
              title={t("amount_poured")}
              subtitle={`(${t("last_30_days")})`}
              value={stats?.pouredQuantity}
              isLoading={isDataLoading}
              unit={"L"}
            />
            <StatCard title={t("number_products")} value={line.products?.length || "-"} />
          </div>
        </div>
      </section>

      <div className="bar-track-line-serving-size">
        {line.products.length > 0 ? (
          <Table
            columns={[
              { key: "title", label: t("name") },
              { key: "size", label: t("size") },
              { key: "price", label: t("price") },
            ]}
            data={line?.products?.flatMap((product) => ({
              size: `${product.servingSize} mL`,
              title: product.title,
              price: `${product.price ?? 0} €`,
            }))}
            title={t("your_products")}
          />
        ) : (
          <div className="bar-track-line-serving-size-empty">
            <Lottie
              animationData={noDatas}
              speed={1}
              className="bar-track-line-serving-size-empty-lottie"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default BartrackLineDetails;
